import React, { useState, useEffect } from 'react';
import { generarUUID } from './generadorUUID';
import config from '../config.json';

interface LanguageSelectionProps {
  sessionUUID: string | null;
  show: boolean;
  onClose: () => void;
  tableName: string;
  columns: any[];
  onGenerate: (framework: string) => void;
}

const LanguageSelection: React.FC<LanguageSelectionProps> = ({
  sessionUUID,
  onClose,
  onGenerate,
  show,
  tableName,
  columns
}) => {
  const [language, setLanguage] = useState<string | null>(null);
  const [framework, setFramework] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const CALLER_URL = config.SERVER_URL_CALLER;
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success',
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ show: true, message, type });

    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleLanguageChange = async (selectedLanguage: string) => {
    setLanguage(selectedLanguage);
    setFramework(null);

    // Si selecciona Java, ejecutar Spring y seguir mostrando opciones
    if (selectedLanguage === 'Java') {
      await callGenerateSpringCrud();
      // NO cerrar, dejar que el usuario seleccione Angular o Vue
      return;
    }
  };

  const fetchTableData = async (tableNameParam?: string, columnsParam?: any[], frameworkParam?: string) => {
    try {
      const payload = {
        tableName: tableNameParam || tableName,
        columns: columnsParam || columns,
        framework: frameworkParam || 'Spring',
        databaseName: sessionUUID
      };

      const response = await fetch(CALLER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      let data: any = null;

      try {
        data = JSON.parse(text);
      } catch {
        showToast(text || 'Respuesta no válida del servidor', 'error');
        return;
      }

      if (data.message) {
        showToast(data.message, data.success ? 'success' : 'error');
      } 
      else if (!data.tableName || !data.columns) {
        if (data.generatorResult) console.log('Generator result:', data.generatorResult);
        showToast('Faltan tableName o columns en la respuesta de Caller.', 'error');
      }

      return data;
    } catch (error) {
      console.error('Error al obtener datos desde CallerLenguage:', error);
      showToast('Error al obtener datos desde CallerLenguage.', 'error');
    }
  };

  const callGenerateAngularCrud = async () => {
    if (!tableName || columns.length === 0) {
      showToast('No hay tableName o columns para Angular.', 'error');
      return { success: false, message: 'No hay tableName o columns para Angular.' };
    }

    try {
      const payload = { tableName, columns, framework: 'Angular', databaseName: sessionUUID };
      const response = await fetch(CALLER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      if (!text.trim()) return { success: false, message: 'Respuesta vacía del servidor.' };

      try {
        const result = JSON.parse(text);
        console.log('Respuesta Angular (via caller):', result);
        return { 
          success: true, 
          message: result.message || 'Proyecto Angular generado correctamente.'
        };
      } catch {
        return { success: false, message: 'Respuesta de Angular no es JSON válido.' };
      }
    } catch (error) {
      console.error('Error Angular:', error);
      return { success: false, message: 'Error generando proyecto Angular.' };
    }
  };

  const callGenerateSpringCrud = async () => {
    if (!tableName || columns.length === 0) {
      showToast('No hay tableName o columns para Spring.', 'error');
      return;
    }

    try {
      const response = await fetch(CALLER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ tableName, columns, framework: 'Spring', databaseName: sessionUUID }),
      });

      const text = await response.text();
      if (!text.trim()) return;

      try {
        const result = JSON.parse(text);
        console.log('Respuesta Spring:', result);
        
        if (result.message) {
          showToast(result.message, result.success ? 'success' : 'error');
        } else {
          showToast('Proyecto Spring Boot generado correctamente.');
        }
      } catch {
        showToast('Respuesta de Spring no es JSON válido.', 'error');
      }
    } catch (error) {
      console.error('Error Spring:', error);
      showToast('Error generando proyecto Spring.', 'error');
    }
  };

  const callGenerateVueCrud = async () => {
    if (!tableName || columns.length === 0) {
      showToast('No hay tableName o columns para Vue.', 'error');
      return { success: false, message: 'No hay tableName o columns para Vue.' };
    }

    try {
      const payload = { tableName, columns, framework: 'Vue', databaseName: sessionUUID };
      const response = await fetch(CALLER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      if (!text.trim()) return { success: false, message: 'Respuesta vacía del servidor.' };

      try {
        const result = JSON.parse(text);
        console.log('Respuesta Vue (via caller):', result);
        return { 
          success: true, 
          message: result.message || 'Proyecto Vue generado correctamente.'
        };
      } catch {
        return { success: false, message: 'Respuesta de Vue no es JSON válido.' };
      }
    } catch (error) {
      console.error('Error Vue:', error);
      return { success: false, message: 'Error generando proyecto Vue.' };
    }
  };

  const handleFrameworkSelection = async (selectedFramework: string) => {
    setFramework(selectedFramework);
    setIsGenerating(true);
    
    // Ejecutar la generación correspondiente y guardar el resultado
    let result;
    if (selectedFramework === 'Angular') {
      result = await callGenerateAngularCrud();
      // Mostrar toast inmediatamente para Angular
      if (result && result.message) {
        showToast(result.message, result.success ? 'success' : 'error');
      }
    } else if (selectedFramework === 'Vue') {
      result = await callGenerateVueCrud();
      // Mostrar toast inmediatamente para Vue
      if (result && result.message) {
        showToast(result.message, result.success ? 'success' : 'error');
      }
    }
    
    // Guardar el resultado
    if (result) {
      setGenerationResult(result);
    }
    
    setIsGenerating(false);
    
    // Mostrar modal de guardar después de completar la generación
    setShowSaveModal(true);
  };

  const handleSave = () => {
    if (!sessionUUID || !language) {
      showToast('No se pudo guardar la configuración.', 'error');
      return;
    }

    localStorage.setItem('projectConfig', JSON.stringify({ 
      sessionUUID, 
      language, 
      architecture: 'MVC',
      framework 
    }));

    // Mostrar el toast de configuración guardada
    showToast(`Configuración guardada: ${language} - MVC${framework ? ' - ' + framework : ''}`);
    
    handleCloseAll();
  };

  const handleCancel = () => {
    handleCloseAll();
  };

  const handleCloseAll = () => {
    setLanguage(null);
    setFramework(null);
    setShowSaveModal(false);
    setGenerationResult(null);
    setIsGenerating(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div>
      {!showSaveModal && !isGenerating && (
        <div className="modal-overlay">
          <div className="modal-language-architecture">
            <h2>Configura tu proyecto</h2>

            {!language && (
              <div className="language-selection">
                <button onClick={() => handleLanguageChange('Java')}>
                  <img src="/assets/java.svg" alt="Java Logo" />Java
                </button>
                <button onClick={() => handleLanguageChange('PHP')}>
                  <img src="/assets/php_logo.svg" alt="PHP Logo" />PHP
                </button>
                <button onClick={handleCloseAll} className="close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            {(language === 'PHP' || language === 'Java') && !framework && (
              <div className="framework-selection">
                <h3>Selecciona el framework para {language} + MVC</h3>
                <button onClick={() => handleFrameworkSelection('Angular')}>Angular</button>
                <button onClick={() => handleFrameworkSelection('Vue')}>Vue</button>
                <button onClick={() => setLanguage(null)} className="btn btn-secondary back-button">
                  <i className="fas fa-arrow-left"></i> Regresar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isGenerating && (
        <div className="modal-overlay">
          <div className="modal-language-architecture">
            <div className="text-center p-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Generando...</span>
              </div>
              <h3>Generando proyecto {framework}...</h3>
              <p>Por favor espera un momento</p>
            </div>
          </div>
        </div>
      )}

      {showSaveModal && (
        <>
          <div className="modal-backdrop fade show custom-backdrop"></div>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Guardar Configuración</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={handleCancel}></button>
                </div>
                <div className="modal-body">
                  <p>
                    ¿Estás seguro de que deseas guardar esta configuración?
                    <br />
                    <strong>Lenguaje:</strong> {language}<br />
                    <strong>Arquitectura:</strong> MVC<br />
                    {framework && (<><strong>Framework:</strong> {framework}</>)}
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancelar</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave}>Aceptar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {toast.show && (
        <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 9999 }}>
          <div className={`toast show text-bg-${toast.type === 'success' ? 'success' : 'danger'}`}>
            <div className="toast-header">
              <strong className="me-auto">{toast.type === 'success' ? 'Correcto' : 'Error'}</strong>
              <button
                type="button"
                className="btn-close"
                onClick={() => setToast(prev => ({ ...prev, show: false }))}
              />
            </div>
            <div className="toast-body">{toast.message}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelection;